import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import _ from 'lodash';

/**
 * View state kept in the query string, so a refresh or a pasted link lands on the same view.
 * Values equal to their default are left out of the URL; the default's type drives parsing.
 *
 * @param {Object} defaults — stable (module-level) object of param name → default value
 * @returns {[Object, Function]} current values, and a patch function taking a partial object
 */
export const useSearchParamsState = defaults => {
  const history = useHistory();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const state = _.mapValues(defaults, (fallback, name) => parseSearchParam(params.get(name), fallback));

  const patch = useCallback(
    changes => {
      const next = new URLSearchParams(history.location.search);
      Object.entries(changes).forEach(([name, value]) => {
        if (value == null || value === '' || value === defaults[name]) next.delete(name);
        else next.set(name, value === true ? '1' : String(value));
      });
      const query = next.toString();
      history.replace({ pathname: history.location.pathname, search: query ? `?${query}` : '' });
    },
    [history, defaults]
  );

  return [state, patch];
};

export const parseSearchParam = (raw, fallback) => {
  if (raw == null) return fallback;
  if (typeof fallback === 'number') return Number.isFinite(Number(raw)) ? Number(raw) : fallback;
  if (typeof fallback === 'boolean') return raw === '1' || raw === 'true';
  return raw;
};
